import type {
  CustomerOrderPaymentStageDto,
  CustomerOrderPaymentVariant,
} from '@/api';

import { daysBetween } from './dates';
import { formatDate, formatMoney, toNumber } from './format';

/*
 * График оплаты заказа 1С - этапы `ЭтапыГрафикаОплаты`. Подписи вариантов - как
 * в интерфейсе УТ. Предоплата - этапы до обеспечения и до отгрузки, остальное -
 * оплата после отгрузки или независимо от неё. Оплачен ли заказ, считает сервер
 * по документам оплаты 1С - модель оплаты отгрузки (ADR-014), не этапы графика.
 */

export const PAYMENT_VARIANTS: Record<CustomerOrderPaymentVariant, string> = {
  BeforeReserve: 'Оплата до обеспечения',
  BeforeShipment: 'Оплата до отгрузки',
  AfterShipment: 'Оплата после отгрузки',
  RegardlessOfShipment: 'Оплата независимо от отгрузки',
};

const PREPAYMENT_VARIANTS: ReadonlyArray<CustomerOrderPaymentVariant> = [
  'BeforeReserve',
  'BeforeShipment',
];

/** Этап - предоплата: до обеспечения или до отгрузки. */
export const isPrepaymentStage = (
  stage: CustomerOrderPaymentStageDto,
): boolean => PREPAYMENT_VARIANTS.includes(stage.variant);

/** Сколько и к какому сроку ждём по группе этапов. */
export interface PaymentDue {
  amount: number;
  /** Последний срок среди этапов группы; null - сроки не заданы. */
  date: string | null;
}

const summarize = (
  stages: ReadonlyArray<CustomerOrderPaymentStageDto>,
): PaymentDue | null => {
  if (stages.length === 0) return null;

  const dates = stages
    .map((stage) => stage.date)
    .filter((date): date is string => Boolean(date))
    .sort();
  const amount = stages.reduce((sum, stage) => sum + toNumber(stage.amount), 0);

  return {
    amount: Math.round(amount * 100) / 100,
    date: dates.length > 0 ? dates[dates.length - 1] : null,
  };
};

/** «Предоплата до» и «Оплата ожидается» - по этапам графика заказа; null - этапов такого вида нет. */
export const summarizePaymentSchedule = (
  stages: ReadonlyArray<CustomerOrderPaymentStageDto>,
): { prepayment: PaymentDue | null; payment: PaymentDue | null } => ({
  prepayment: summarize(stages.filter(isPrepaymentStage)),
  payment: summarize(stages.filter((stage) => !isPrepaymentStage(stage))),
});

/** Последний срок графика - к нему заказ должен быть оплачен целиком; null - сроков нет. */
export const lastPaymentDate = (
  stages: ReadonlyArray<CustomerOrderPaymentStageDto>,
): string | null => summarize(stages)?.date ?? null;

/** На сколько дней срок просрочен; null - срока нет или он не наступил. */
export const overdueDays = (
  date: string | null | undefined,
  today: string,
): number | null => (date && date < today ? daysBetween(date, today) : null);

/** Срок и сумма одной строкой: «05.09.2026 · 600 ₽». */
export const formatPaymentDue = (due: PaymentDue): string =>
  due.date
    ? `${formatDate(due.date)} · ${formatMoney(due.amount)}`
    : `${formatMoney(due.amount)}, срок не задан`;
